/**
 * Screen-space grid for hover hit tests.
 *
 * Points and segment endpoints are projected once. A query only inspects the
 * cells that can contain a hit, so a still camera does not project the graph
 * again on every pointer move.
 */

const CELL_PX = 64;

export interface ScreenPoint {
  x: number;
  y: number;
}

interface ScreenSegment {
  ax: number;
  ay: number;
  bx: number;
  by: number;
}

export interface ScreenHitGrid {
  nearestPoint(x: number, y: number, radius: number): number | null;
  nearestSegment(x: number, y: number, radius: number, hidden?: Uint8Array): number | null;
  /** Every segment within `radius`, skipping indexes marked in `hidden`. */
  segmentsNear(x: number, y: number, radius: number, hidden?: Uint8Array): number[];
  /** Points inside an axis-aligned screen rectangle. Edges count as inside. */
  pointsInRect(minX: number, minY: number, maxX: number, maxY: number): number[];
  /** Segments that cross an axis-aligned screen rectangle. Edges count. */
  segmentsInRect(minX: number, minY: number, maxX: number, maxY: number): number[];
  /** Points within `radius` of a screen position. */
  pointsNear(x: number, y: number, radius: number): number[];
}

export function buildScreenHitGrid(
  points: readonly ScreenPoint[],
  segments: readonly (readonly [number, number])[],
): ScreenHitGrid {
  const pointBuckets = new Map<number, number[]>();
  const segmentBuckets = new Map<number, number[]>();
  const drawn: Array<ScreenSegment | undefined> = new Array(segments.length);

  for (let index = 0; index < points.length; index++) {
    const point = points[index];
    if (!point) continue;
    pushBucket(pointBuckets, cellKey(point.x, point.y), index);
  }

  for (let index = 0; index < segments.length; index++) {
    const pair = segments[index];
    if (!pair) continue;
    const a = points[pair[0]];
    const b = points[pair[1]];
    if (!a || !b) continue;
    const segment: ScreenSegment = { ax: a.x, ay: a.y, bx: b.x, by: b.y };
    drawn[index] = segment;
    const x0 = Math.floor(Math.min(a.x, b.x) / CELL_PX);
    const x1 = Math.floor(Math.max(a.x, b.x) / CELL_PX);
    const y0 = Math.floor(Math.min(a.y, b.y) / CELL_PX);
    const y1 = Math.floor(Math.max(a.y, b.y) / CELL_PX);
    for (let ix = x0; ix <= x1; ix++) {
      for (let iy = y0; iy <= y1; iy++) pushBucket(segmentBuckets, pack(ix, iy), index);
    }
  }

  const pointSeen = new Uint32Array(points.length);
  const segmentSeen = new Uint32Array(segments.length);
  let stamp = 1;

  function nextStamp(): number {
    stamp += 1;
    if (stamp > 0xffffffff) {
      pointSeen.fill(0);
      segmentSeen.fill(0);
      stamp = 1;
    }
    return stamp;
  }

  return {
    nearestPoint(x, y, radius) {
      const mark = nextStamp();
      let best: number | null = null;
      let bestD = radius;
      visit(pointBuckets, x, y, radius, (index) => {
        if (pointSeen[index] === mark) return;
        pointSeen[index] = mark;
        const point = points[index];
        if (!point) return;
        const distance = Math.hypot(point.x - x, point.y - y);
        if (distance <= bestD && (best === null || distance < bestD || index > best)) {
          bestD = distance;
          best = index;
        }
      });
      return best;
    },
    nearestSegment(x, y, radius, hidden) {
      const mark = nextStamp();
      let best: number | null = null;
      let bestD = radius;
      visit(segmentBuckets, x, y, radius, (index) => {
        if (segmentSeen[index] === mark) return;
        segmentSeen[index] = mark;
        if (hidden && hidden[index]) return;
        const segment = drawn[index];
        if (!segment) return;
        const distance = distanceToSegment(x, y, segment.ax, segment.ay, segment.bx, segment.by);
        if (distance <= bestD && (best === null || distance < bestD || index > best)) {
          bestD = distance;
          best = index;
        }
      });
      return best;
    },
    segmentsNear(x, y, radius, hidden) {
      const mark = nextStamp();
      const found: number[] = [];
      visit(segmentBuckets, x, y, radius, (index) => {
        if (segmentSeen[index] === mark) return;
        segmentSeen[index] = mark;
        if (hidden && hidden[index]) return;
        const segment = drawn[index];
        if (!segment) return;
        const distance = distanceToSegment(x, y, segment.ax, segment.ay, segment.bx, segment.by);
        if (distance <= radius) found.push(index);
      });
      return found;
    },
    pointsInRect(minX, minY, maxX, maxY) {
      const xLo = Math.min(minX, maxX);
      const xHi = Math.max(minX, maxX);
      const yLo = Math.min(minY, maxY);
      const yHi = Math.max(minY, maxY);
      const mark = nextStamp();
      const found: number[] = [];
      const ix0 = Math.floor(xLo / CELL_PX);
      const ix1 = Math.floor(xHi / CELL_PX);
      const iy0 = Math.floor(yLo / CELL_PX);
      const iy1 = Math.floor(yHi / CELL_PX);
      for (let ix = ix0; ix <= ix1; ix++) {
        for (let iy = iy0; iy <= iy1; iy++) {
          const list = pointBuckets.get(pack(ix, iy));
          if (!list) continue;
          for (let i = 0; i < list.length; i++) {
            const index = list[i]!;
            if (pointSeen[index] === mark) continue;
            pointSeen[index] = mark;
            const point = points[index];
            if (!point) continue;
            if (point.x >= xLo && point.x <= xHi && point.y >= yLo && point.y <= yHi) found.push(index);
          }
        }
      }
      return found;
    },
    segmentsInRect(minX, minY, maxX, maxY) {
      const xLo = Math.min(minX, maxX);
      const xHi = Math.max(minX, maxX);
      const yLo = Math.min(minY, maxY);
      const yHi = Math.max(minY, maxY);
      const mark = nextStamp();
      const found: number[] = [];
      const ix0 = Math.floor(xLo / CELL_PX);
      const ix1 = Math.floor(xHi / CELL_PX);
      const iy0 = Math.floor(yLo / CELL_PX);
      const iy1 = Math.floor(yHi / CELL_PX);
      for (let ix = ix0; ix <= ix1; ix++) {
        for (let iy = iy0; iy <= iy1; iy++) {
          const list = segmentBuckets.get(pack(ix, iy));
          if (!list) continue;
          for (let i = 0; i < list.length; i++) {
            const index = list[i]!;
            if (segmentSeen[index] === mark) continue;
            segmentSeen[index] = mark;
            const segment = drawn[index];
            if (!segment) continue;
            if (segmentHitsAabb(segment.ax, segment.ay, segment.bx, segment.by, xLo, yLo, xHi, yHi)) found.push(index);
          }
        }
      }
      return found;
    },
    pointsNear(x, y, radius) {
      const mark = nextStamp();
      const found: number[] = [];
      const r2 = radius * radius;
      visit(pointBuckets, x, y, radius, (index) => {
        if (pointSeen[index] === mark) return;
        pointSeen[index] = mark;
        const point = points[index];
        if (!point) return;
        const dx = point.x - x;
        const dy = point.y - y;
        if (dx * dx + dy * dy <= r2) found.push(index);
      });
      return found;
    },
  };
}

/**
 * Segments the erase brush covers while moving from one sample to the next.
 * Disks along the stroke overlap, so a fast drag still hits the lines between samples.
 */
export function segmentsUnderBrush(
  grid: ScreenHitGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number,
  hidden?: Uint8Array,
): number[] {
  const found: number[] = [];
  const seen = new Set<number>();
  const distance = Math.hypot(x1 - x0, y1 - y0);
  const step = Math.max(8, radius);
  const steps = Math.max(1, Math.ceil(distance / step));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const hits = grid.segmentsNear(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, radius, hidden);
    for (const index of hits) {
      if (seen.has(index)) continue;
      seen.add(index);
      found.push(index);
    }
  }
  return found;
}

/**
 * Points the circle select covers while moving from one sample to the next.
 * Disks along the stroke overlap, so a fast drag still hits the gap between samples.
 */
export function pointsUnderBrush(
  grid: ScreenHitGrid,
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  radius: number,
): number[] {
  const found: number[] = [];
  const seen = new Set<number>();
  const distance = Math.hypot(x1 - x0, y1 - y0);
  const step = Math.max(8, radius);
  const steps = Math.max(1, Math.ceil(distance / step));
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const hits = grid.pointsNear(x0 + (x1 - x0) * t, y0 + (y1 - y0) * t, radius);
    for (const index of hits) {
      if (seen.has(index)) continue;
      seen.add(index);
      found.push(index);
    }
  }
  return found;
}

function pushBucket(buckets: Map<number, number[]>, key: number, index: number): void {
  const list = buckets.get(key);
  if (list) list.push(index);
  else buckets.set(key, [index]);
}

function cellKey(x: number, y: number): number {
  return pack(Math.floor(x / CELL_PX), Math.floor(y / CELL_PX));
}

function pack(ix: number, iy: number): number {
  return (ix + 1048576) * 2097152 + (iy + 1048576);
}

function visit(
  buckets: Map<number, number[]>,
  x: number,
  y: number,
  radius: number,
  each: (index: number) => void,
): void {
  const x0 = Math.floor((x - radius) / CELL_PX);
  const x1 = Math.floor((x + radius) / CELL_PX);
  const y0 = Math.floor((y - radius) / CELL_PX);
  const y1 = Math.floor((y + radius) / CELL_PX);
  for (let ix = x0; ix <= x1; ix++) {
    for (let iy = y0; iy <= y1; iy++) {
      const list = buckets.get(pack(ix, iy));
      if (!list) continue;
      for (let i = 0; i < list.length; i++) each(list[i]!);
    }
  }
}

/** Inclusive. A segment that only touches the edge counts. */
function segmentHitsAabb(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  minX: number,
  minY: number,
  maxX: number,
  maxY: number,
): boolean {
  let t0 = 0;
  let t1 = 1;
  const dx = bx - ax;
  const dy = by - ay;
  const p = [-dx, dx, -dy, dy];
  const q = [ax - minX, maxX - ax, ay - minY, maxY - ay];
  for (let i = 0; i < 4; i++) {
    const pi = p[i]!;
    const qi = q[i]!;
    if (pi === 0) {
      if (qi < 0) return false;
      continue;
    }
    const t = qi / pi;
    if (pi < 0) {
      if (t > t1) return false;
      if (t > t0) t0 = t;
    } else {
      if (t < t0) return false;
      if (t < t1) t1 = t;
    }
  }
  return true;
}

function distanceToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  const t = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / len2));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}
